import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import legionsTimeline from "../data/LegionsTimeline.json";
import militaryDossiers from "../data/militaryDossiers.json";

const PERIOD_LABELS = {
  invasion: "Claudian Invasion",
  boudica: "Boudican Revolt",
  flavian: "Flavian Consolidation",
  hadrian: "Hadrianic Frontier",
  antonine: "Antonine Advance",
  late2nd: "Late 2nd-Century Strain",
  severan: "Severan Crisis",
  late: "Late Roman Transformation",
};

const LEGION_LABELS = {
  II: "Legio II Augusta",
  XX: "Legio XX Valeria Victrix",
  VI: "Legio VI Victrix",
};

const ARCHIVE_SECTIONS = [
  {
    title: "Campaign Dossiers",
    text: "Military movement, invasion routes, revolt, civil war, and imperial campaigns.",
    filter: (item) =>
      ["invasion", "boudica", "flavian", "severan"].includes(item.periodId),
  },
  {
    title: "Fortress Profiles",
    text: "The fixed military places that held Roman power in the landscape.",
    filter: (item) =>
      /fortress|Deva|Eboracum|Isca|Carpow|Rutupiae|Camulodunum|Alchester/i.test(
        `${item.name} ${item.role}`
      ),
  },
  {
    title: "Frontier Systems",
    text: "Walls, northern routes, detachments, and the military edge of empire.",
    filter: (item) =>
      ["hadrian", "antonine", "late"].includes(item.periodId) ||
      /Wall|frontier|northern/i.test(`${item.name} ${item.role}`),
  },
  {
    title: "Movement Routes & Supply",
    text: "Roads, corridors, ports, river approaches, and logistical chains.",
    filter: (item) =>
      /route|supply|corridor|approach|Landing|support/i.test(
        `${item.name} ${item.role}`
      ),
  },
  {
    title: "Imperial Crisis & Reform",
    text: "Political strain, civil war, Severan pressure, and the changing army.",
    filter: (item) =>
      ["late2nd", "severan", "late"].includes(item.periodId),
  },
];

function getDossier(id) {
  return militaryDossiers.find((d) => d.id === id);
}

function groupByLegion(items) {
  return items.reduce((acc, item) => {
    const key = item.legionId || "Other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

function groupByPeriod(items) {
  return items.reduce((acc, item) => {
    const key = item.periodId || "other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

export default function RomanMilitaryArchive() {
  const [activeLegion, setActiveLegion] = useState("all");
  const [activeSection, setActiveSection] = useState("all");

  const enrichedItems = useMemo(() => {
    return legionsTimeline.map((item) => ({
      ...item,
      dossier: getDossier(item.id),
    }));
  }, []);

  const filteredItems = useMemo(() => {
    return enrichedItems.filter((item) => {
      const legionMatch = activeLegion === "all" || item.legionId === activeLegion;
      const sectionMatch =
        activeSection === "all" ||
        ARCHIVE_SECTIONS.find((section) => section.title === activeSection)?.filter(item);
      return legionMatch && sectionMatch;
    });
  }, [enrichedItems, activeLegion, activeSection]);

  const byLegion = groupByLegion(filteredItems);
  const byPeriod = groupByPeriod(enrichedItems);

  return (
    <main className="min-h-screen bg-[#0b0a08] text-stone-100">
      <section className="border-b border-amber-900/40 bg-gradient-to-b from-[#14100c] to-[#0b0a08]">
        <div className="mx-auto max-w-7xl px-5 py-14">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.28em] text-amber-400">
            Roman Storyworld Library
          </p>

          <h1 className="max-w-5xl text-4xl font-bold leading-tight text-amber-100 md:text-6xl">
            Roman Military Archive
          </h1>

          <p className="mt-6 max-w-4xl text-lg leading-8 text-stone-300">
            A guided archive for the legions, campaigns, fortresses, frontiers,
            movement routes, supply systems, and imperial crises that shaped Roman
            Britain. Each entry opens as a dossier, turning the timeline into a
            readable military landscape.
          </p>

          <div className="mt-8 grid gap-4 md:grid-cols-4">
            <div className="rounded-2xl border border-amber-800/40 bg-black/25 p-5">
              <div className="text-3xl font-bold text-amber-300">{legionsTimeline.length}</div>
              <div className="mt-1 text-sm text-stone-400">timeline anchors</div>
            </div>
            <div className="rounded-2xl border border-amber-800/40 bg-black/25 p-5">
              <div className="text-3xl font-bold text-amber-300">3</div>
              <div className="mt-1 text-sm text-stone-400">main legions</div>
            </div>
            <div className="rounded-2xl border border-amber-800/40 bg-black/25 p-5">
              <div className="text-3xl font-bold text-amber-300">8</div>
              <div className="mt-1 text-sm text-stone-400">campaign phases</div>
            </div>
            <div className="rounded-2xl border border-amber-800/40 bg-black/25 p-5">
              <div className="text-3xl font-bold text-amber-300">1</div>
              <div className="mt-1 text-sm text-stone-400">connected archive</div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-10">
        <div className="mb-8 grid gap-4 lg:grid-cols-[1fr_1fr]">
          <div className="rounded-2xl border border-stone-800 bg-[#15120f] p-5">
            <h2 className="mb-4 text-xl font-bold text-amber-200">Filter by legion</h2>
            <div className="flex flex-wrap gap-2">
              {["all", "II", "XX", "VI"].map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveLegion(key)}
                  className={`rounded-full border px-4 py-2 text-sm transition ${
                    activeLegion === key
                      ? "border-amber-400 bg-amber-700 text-white"
                      : "border-stone-700 bg-stone-900 text-stone-300 hover:border-amber-500"
                  }`}
                >
                  {key === "all" ? "All legions" : LEGION_LABELS[key]}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-stone-800 bg-[#15120f] p-5">
            <h2 className="mb-4 text-xl font-bold text-amber-200">Filter by archive type</h2>
            <div className="flex flex-wrap gap-2">
              {["all", ...ARCHIVE_SECTIONS.map((s) => s.title)].map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveSection(key)}
                  className={`rounded-full border px-4 py-2 text-sm transition ${
                    activeSection === key
                      ? "border-amber-400 bg-amber-700 text-white"
                      : "border-stone-700 bg-stone-900 text-stone-300 hover:border-amber-500"
                  }`}
                >
                  {key === "all" ? "All archive types" : key}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="mb-12 rounded-2xl border border-amber-900/40 bg-[#18130f] p-6">
          <h2 className="mb-5 text-2xl font-bold text-amber-200">Archive pathways</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {ARCHIVE_SECTIONS.map((section) => (
              <button
                key={section.title}
                type="button"
                onClick={() => setActiveSection(section.title)}
                className="rounded-xl border border-stone-800 bg-stone-950/60 p-4 text-left transition hover:border-amber-500"
              >
                <h3 className="text-base font-bold text-amber-300">{section.title}</h3>
                <p className="mt-2 text-sm leading-6 text-stone-400">{section.text}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="mb-5 text-2xl font-bold text-amber-200">Chronology at a glance</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Object.entries(byPeriod).map(([periodId, items]) => (
              <div key={periodId} className="rounded-xl border border-stone-800 bg-[#15120f] p-4">
                <h3 className="font-bold text-amber-300">{PERIOD_LABELS[periodId] || periodId}</h3>
                <p className="mt-2 text-sm text-stone-400">{items.length} archive entries</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-5 text-3xl font-bold text-amber-100">Legions, places, and campaign dossiers</h2>

          {Object.entries(byLegion).map(([legionId, items]) => (
            <div key={legionId} className="mb-12">
              <h3 className="mb-4 text-2xl font-bold text-amber-200">
                {LEGION_LABELS[legionId] || `Legio ${legionId}`}
              </h3>

              <div className="grid gap-5 lg:grid-cols-2">
                {items.map((item) => (
                  <article
                    key={item.id}
                    className="rounded-2xl border border-stone-800 bg-[#171411] p-5 shadow-lg transition hover:border-amber-600"
                  >
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-amber-900/50 px-3 py-1 text-xs font-semibold text-amber-200">
                        {PERIOD_LABELS[item.periodId] || item.periodId}
                      </span>
                      <span className="rounded-full bg-stone-800 px-3 py-1 text-xs text-stone-300">
                        {item.role}
                      </span>
                    </div>

                    <h4 className="text-xl font-bold text-amber-300">{item.name}</h4>
                    <p className="mt-3 text-sm leading-7 text-stone-300">{item.summary}</p>

                    <div className="mt-5 flex flex-wrap gap-3">
                      <Link
                        to={`/military/${item.id}`}
                        className="rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-600"
                      >
                        Read campaign dossier →
                      </Link>

                      <span className="rounded-lg border border-stone-700 px-4 py-2 text-sm text-stone-400">
                        {item.lat.toFixed(2)}, {item.lng.toFixed(2)}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </section>
      </section>
    </main>
  );
}
